<template>
  <div>
    <!-- Заголовок -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900">{{ pageTitle }}</h1>
      <div v-if="isClinicManagerMode" class="mt-3 max-w-md">
        <label class="block text-sm text-gray-600 mb-2">Врач</label>
        <select
          v-model="selectedDoctorId"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Выберите врача</option>
          <option
            v-for="doctor in managerDoctors"
            :key="doctor.id"
            :value="String(doctor.id)"
          >
            {{ getDoctorDisplayName(doctor) }}
          </option>
        </select>
      </div>
    </div>

    <!-- Horizontal scrollable calendar strip -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
        <!-- Scrollable calendar strip -->
        <div 
          ref="calendarStrip" 
          class="overflow-x-auto scroll-smooth cursor-grab active:cursor-grabbing"
          style="scrollbar-width: none; -ms-overflow-style: none;"
          @scroll="handleScroll"
          @mousedown="startDrag"
          @mousemove="handleDrag"
          @mouseup="endDrag"
          @mouseleave="endDrag"
        >
          <div class="flex space-x-2 px-6 py-4" style="width: max-content;">
            <div 
              v-for="day in calendarDays" 
              :key="day.dateString"
              @click="selectDay(day)"
              class="flex-shrink-0 relative p-3 rounded-xl border cursor-pointer transition-all duration-200 hover:shadow-md min-w-20 select-none"
              :class="{
                'border-blue-500 bg-blue-50 shadow-md': isSelectedDay(day),
                'border-blue-300 bg-blue-25': isToday(day) && !isSelectedDay(day),
                'border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300': !isSelectedDay(day) && !isToday(day)
              }"
            >
              <!-- Day header -->
              <div class="text-center">
                <div class="text-xs font-medium text-gray-600 mb-1">{{ day.name }}</div>
                <div 
                  class="text-lg font-bold mb-2 transition-colors"
                  :class="{
                    'text-blue-600': isSelectedDay(day),
                    'text-blue-500': isToday(day) && !isSelectedDay(day),
                    'text-gray-900': !isSelectedDay(day) && !isToday(day)
                  }"
                >
                  {{ day.day }}
                </div>
                <div class="text-xs text-gray-500 mb-2">{{ day.month }}</div>
              </div>
              
              <!-- Appointments indicator -->
              <div class="text-center">
                <div v-if="getDayAppointmentCount(day) > 0" class="mb-1">
                  <div class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                       :class="{
                         'bg-blue-100 text-blue-800': isSelectedDay(day),
                         'bg-blue-50 text-blue-700': isToday(day) && !isSelectedDay(day),
                         'bg-gray-100 text-gray-700': !isSelectedDay(day) && !isToday(day)
                       }">
                    {{ getDayAppointmentCount(day) }}
                  </div>
                </div>
                
                <!-- Mini appointment indicators -->
                <div class="flex justify-center space-x-1">
                  <div 
                    v-for="appointment in getDayAppointments(day).slice(0, 4)" 
                    :key="appointment.id"
                    class="w-1.5 h-1.5 rounded-full"
                    :class="{
                      'bg-green-400': appointment.status === 'completed',
                      'bg-blue-400': appointment.status === 'scheduled',
                      'bg-yellow-400': appointment.status === 'in_progress',
                      'bg-red-400': appointment.status === 'cancelled'
                    }"
                  ></div>
                  <div 
                    v-if="getDayAppointmentCount(day) > 4"
                    class="text-xs text-gray-500 font-medium"
                  >
                    +
                  </div>
                </div>
              </div>
              
              <!-- Selection indicator -->
              <div 
                v-if="isSelectedDay(day)"
                class="absolute bottom-0 left-0 right-0 h-1 bg-blue-500 rounded-b-xl"
              ></div>
              
              <!-- Today indicator -->
              <div 
                v-if="isToday(day)"
                class="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full today-indicator"
              ></div>
            </div>
          </div>
        </div>
        
        <!-- Scroll indicator and navigation -->
        <div class="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm text-gray-600">{{ currentMonthYear }}</span>
            <button 
              @click="showCalendarPicker = true"
              class="flex items-center space-x-2 px-3 py-1 text-xs bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 transition-colors font-medium"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              <span>{{ $t('common.calendar') }}</span>
            </button>
          </div>
          
          <!-- Scroll hint -->
          <div class="text-center">
            <span class="text-xs text-gray-500">
              <svg class="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16l-4-4m0 0l4-4m-4 4h18"/>
              </svg>
              {{ $t('doctor.dragToScroll') }}
              <svg class="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
              </svg>
            </span>
          </div>
        </div>
      </div>
      
      <!-- Day schedule with all time slots -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-900">
              {{ formatSelectedDate() }}
            </h2>
            <button 
              @click="goToToday"
              class="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              {{ $t('common.today') }}
            </button>
          </div>
        </div>
        
        <div class="p-6">
          <!-- Loading state -->
          <div v-if="loadingAppointments || loadingDaySlots" class="flex justify-center p-8">
            <HeartbeatLoader :text="$t('common.loading')" />
          </div>
          
          <!-- No schedule message -->
          <div v-else-if="dayTimeSlots.length === 0" class="text-center p-8">
            <div class="flex flex-col items-center space-y-4">
              <svg class="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6"/>
              </svg>
              <div class="text-gray-500">
                <h3 class="text-lg font-medium mb-2">{{ noScheduleTitle }}</h3>
                <p class="text-sm">{{ noScheduleMessage }}</p>
              </div>
            </div>
          </div>
          
          <!-- Time slots list -->
          <div v-else class="space-y-2">
            <div 
              v-for="slot in dayTimeSlots" 
              :key="slot.time"
              class="group relative flex items-center p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md"
              :class="{
                'border-green-200 bg-green-50': slot.appointment && slot.appointment.status === 'completed',
                'border-blue-200 bg-blue-50': slot.appointment && slot.appointment.status === 'scheduled',
                'border-yellow-200 bg-yellow-50': slot.appointment && slot.appointment.status === 'in_progress',
                'border-red-200 bg-red-50': slot.appointment && slot.appointment.status === 'cancelled',
                'border-orange-200 bg-orange-50': slot.appointment && slot.appointment.status === 'no_show',
                'border-gray-200 bg-gray-50 hover:bg-gray-100': !slot.appointment
              }"
            >
              <!-- Time -->
              <div class="flex items-center space-x-3 w-32">
                <div class="flex items-center space-x-2">
                  <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span class="text-sm font-semibold text-gray-900">
                    {{ slot.time }}
                  </span>
                </div>
              </div>
              
              <!-- Slot content -->
              <div class="flex-1">
                <!-- Empty slot -->
                <div v-if="!slot.appointment" class="flex items-center">
                  <span class="text-gray-500 text-sm">{{ $t('doctor.freeSlot') }}</span>
                </div>
                
                <!-- Appointment slot -->
                <div v-else class="flex items-center justify-between">
                  <div class="flex items-center space-x-3">
                    <!-- Patient avatar -->
                    <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                    </div>
                    
                    <!-- Basic info -->
                    <div>
                      <div class="font-medium text-gray-900">
                        {{ getPatientName(slot.appointment) }}
                      </div>
                      <div class="text-sm text-gray-600">
                        {{ getStatusText(slot.appointment.status) }}
                      </div>
                    </div>
                  </div>
                  
                  <!-- Status badge -->
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                        :class="{
                          'bg-green-100 text-green-800': slot.appointment.status === 'completed',
                          'bg-blue-100 text-blue-800': slot.appointment.status === 'scheduled',
                          'bg-yellow-100 text-yellow-800': slot.appointment.status === 'in_progress',
                          'bg-red-100 text-red-800': slot.appointment.status === 'cancelled',
                          'bg-orange-100 text-orange-800': slot.appointment.status === 'no_show' || slot.appointment.status === 'patient_no_show'
                        }">
                    {{ getStatusText(slot.appointment.status) }}
                  </span>
                </div>
              </div>
              
              <!-- Action buttons for scheduled appointments -->
              <div v-if="!isClinicManagerMode && slot.appointment && slot.appointment.status === 'scheduled'" class="flex items-center space-x-2 ml-4">
                <button 
                  @click="startConsultation(slot.appointment)"
                  class="px-3 py-1 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 transition-colors"
                >
                  {{ $t('appointment.startAppointment') }}
                </button>
                <button 
                  @click="cancelAppointment(slot.appointment)"
                  class="px-3 py-1 bg-white text-red-600 text-xs rounded-md border border-red-300 hover:bg-red-50 transition-colors"
                >
                  {{ $t('common.cancel') }}
                </button>
              </div>
              
              <!-- In progress indicator -->
              <div v-else-if="slot.appointment && slot.appointment.status === 'in_progress'" class="flex items-center space-x-2 ml-4">
                <div class="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                <span class="text-xs text-yellow-600 font-medium">{{ $t('appointment.inProgress') }}</span>
              </div>
              
              <!-- Hover tooltip with details -->
              <div 
                v-if="slot.appointment"
                class="absolute z-10 invisible group-hover:visible bg-gray-900 text-white p-3 rounded-lg shadow-lg -top-16 left-1/2 transform -translate-x-1/2 min-w-64"
              >
                <div class="text-sm">
                  <div class="font-medium mb-1">{{ getPatientName(slot.appointment) }}</div>
                  <div class="text-gray-300 mb-2">{{ slot.time }}</div>
                  <div v-if="slot.appointment.reason" class="text-gray-300">
                    <span class="font-medium">{{ $t('appointment.reason') }}:</span> {{ slot.appointment.reason }}
                  </div>
                  <div v-else class="text-gray-400 italic">{{ $t('appointment.reasonNotSpecified') }}</div>
                </div>
                <!-- Tooltip arrow -->
                <div class="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>


  <!-- Calendar Picker Modal -->
  <div 
    v-if="showCalendarPicker"
    class="fixed inset-0 z-50 overflow-y-auto"
    @click="showCalendarPicker = false"
  >
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="fixed inset-0 bg-black bg-opacity-25 transition-opacity"></div>
      
      <div 
        class="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 transform transition-all"
        @click.stop
      >
        <!-- Calendar Header -->
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-lg font-semibold text-gray-900">{{ $t('doctor.selectDate') }}</h3>
          <button 
            @click="showCalendarPicker = false"
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <!-- Year/Month Navigation -->
        <div class="flex items-center justify-between mb-4">
          <button 
            @click="navigateYear(-1)"
            class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          
          <div class="flex items-center space-x-4">
            <select 
              v-model="pickerMonth" 
              @change="updatePickerCalendar"
              class="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option v-for="(month, index) in monthNames" :key="index" :value="index">
                {{ month }}
              </option>
            </select>
            
            <select 
              v-model="pickerYear" 
              @change="updatePickerCalendar"
              class="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option v-for="year in availableYears" :key="year" :value="year">
                {{ year }}
              </option>
            </select>
          </div>
          
          <button 
            @click="navigateYear(1)"
            class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
        
        <!-- Month navigation -->
        <div class="flex items-center justify-between mb-4">
          <button 
            @click="navigateMonth(-1)"
            class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          
          <h4 class="text-lg font-medium text-gray-900">
            {{ monthNames[pickerMonth] }} {{ pickerYear }}
          </h4>
          
          <button 
            @click="navigateMonth(1)"
            class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
        
        <!-- Calendar Grid -->
        <div class="grid grid-cols-7 gap-1 mb-4">
          <!-- Day headers -->
          <div 
            v-for="day in dayHeaders" 
            :key="day"
            class="p-2 text-center text-xs font-medium text-gray-500"
          >
            {{ day }}
          </div>
          
          <!-- Calendar days -->
          <button
            v-for="day in pickerCalendarDays"
            :key="day.key"
            @click="selectDateFromPicker(day)"
            class="relative p-2 text-sm rounded-lg transition-all duration-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            :class="{
              'text-gray-400': !day.isCurrentMonth,
              'text-gray-900 hover:bg-blue-50': day.isCurrentMonth && !day.isSelected && !day.isToday,
              'bg-blue-600 text-white hover:bg-blue-700': day.isSelected,
              'bg-blue-100 text-blue-600 font-semibold': day.isToday && !day.isSelected,
              'cursor-not-allowed opacity-50': day.isDisabled
            }"
            :disabled="day.isDisabled"
          >
            {{ day.day }}
            
            <!-- Appointment indicators -->
            <div v-if="day.hasAppointments && day.isCurrentMonth" class="absolute bottom-0.5 left-1/2 transform -translate-x-1/2">
              <div class="w-1 h-1 bg-blue-500 rounded-full" :class="{ 'bg-white': day.isSelected }"></div>
            </div>
          </button>
        </div>
        
        <!-- Quick actions -->
        <div class="flex items-center justify-between pt-4 border-t border-gray-200">
          <button 
            @click="goToTodayFromPicker"
            class="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium"
          >
            {{ $t('calendar.today') }}
          </button>
          
          <div class="flex items-center space-x-2">
            <button 
              @click="showCalendarPicker = false"
              class="px-4 py-2 text-sm text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              {{ $t('calendar.cancel') }}
            </button>
            <button 
              @click="confirmDateSelection"
              class="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              {{ $t('calendar.select') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import staffApi from '@/services/staffApi'
import HeartbeatLoader from '@/components/common/HeartbeatLoader.vue'

export default {
  name: 'DoctorSchedule',
  components: {
    HeartbeatLoader
  },
  props: {
    mode: {
      type: String,
      default: 'doctor'
    }
  },
  data() {
    return {
      currentWeekStart: null,
      selectedDate: null,
      weekDays: [],
      appointments: [],
      selectedDayAppointments: [],
      dayTimeSlots: [],
      loadingAppointments: false,
      loadingDaySlots: false, // Отдельный лоадер для загрузки слотов дня
      slotsData: {},
      patientsData: {},
      calendarDays: [],
      currentMonthYear: '',
      isDragging: false,
      dragStartX: 0,
      dragStartScrollLeft: 0,
      showCalendarPicker: false,
      pickerMonth: 0,
      pickerYear: 0,
      availableYears: [],
      pickerCalendarDays: [],
      doctorId: null, // Кешируем ID врача
      cachedDaySlots: {}, // Кеш для загруженных слотов по дням
      currentStaffRole: null,
      managerClinicId: null,
      managerDoctors: [],
      selectedDoctorId: ''
    }
  },
  computed: {
    isClinicManagerMode() {
      return this.mode === 'manager'
    },
    pageTitle() {
      return this.isClinicManagerMode ? 'Расписание врачей' : this.$t('doctor.mySchedule')
    },
    noScheduleTitle() {
      return this.isClinicManagerMode
        ? 'На выбранный день нет расписания врачей'
        : this.$t('doctor.noScheduleForDay')
    },
    noScheduleMessage() {
      return this.isClinicManagerMode
        ? 'В поликлинике нет доступных слотов на выбранную дату.'
        : this.$t('doctor.noScheduleMessage')
    },
    monthNames() {
      return [
        this.$t('calendar.months.january'),
        this.$t('calendar.months.february'),
        this.$t('calendar.months.march'),
        this.$t('calendar.months.april'),
        this.$t('calendar.months.may'),
        this.$t('calendar.months.june'),
        this.$t('calendar.months.july'),
        this.$t('calendar.months.august'),
        this.$t('calendar.months.september'),
        this.$t('calendar.months.october'),
        this.$t('calendar.months.november'),
        this.$t('calendar.months.december')
      ]
    },
    dayHeaders() {
      return [
        this.$t('calendar.weekdaysShort.monday'),
        this.$t('calendar.weekdaysShort.tuesday'),
        this.$t('calendar.weekdaysShort.wednesday'),
        this.$t('calendar.weekdaysShort.thursday'),
        this.$t('calendar.weekdaysShort.friday'),
        this.$t('calendar.weekdaysShort.saturday'),
        this.$t('calendar.weekdaysShort.sunday')
      ]
    },
    weekdayNames() {
      return [
        this.$t('calendar.weekdaysShort.monday'),
        this.$t('calendar.weekdaysShort.tuesday'),
        this.$t('calendar.weekdaysShort.wednesday'),
        this.$t('calendar.weekdaysShort.thursday'),
        this.$t('calendar.weekdaysShort.friday'),
        this.$t('calendar.weekdaysShort.saturday'),
        this.$t('calendar.weekdaysShort.sunday')
      ]
    },
    weekdayNamesWithSunday() {
      return [
        this.$t('calendar.weekdaysShort.sunday'),
        this.$t('calendar.weekdaysShort.monday'),
        this.$t('calendar.weekdaysShort.tuesday'),
        this.$t('calendar.weekdaysShort.wednesday'),
        this.$t('calendar.weekdaysShort.thursday'),
        this.$t('calendar.weekdaysShort.friday'),
        this.$t('calendar.weekdaysShort.saturday')
      ]
    }
  },
  watch: {
    '$i18n.locale'() {
      // Пересоздаем календарь при смене языка
      this.generateWeekDays()
      this.generateCalendarDays()
    },
    selectedDoctorId() {
      if (!this.isClinicManagerMode) return

      this.doctorId = this.selectedDoctorId ? Number(this.selectedDoctorId) : null
      this.cachedDaySlots = {}
      this.fetchAppointments()
      this.fetchDaySlots()
    }
  },
  async created() {
    this.initializeCalendar()
    await this.initializeDoctor()
    this.fetchAppointments()
    this.initializeCalendarPicker()
    
    // Загружаем слоты для текущего выбранного дня (сегодня) после инициализации
    this.$nextTick(() => {
      if (this.doctorId && this.selectedDate) {
        this.fetchDaySlots()
      }
    })
  },
  mounted() {
    // Компонент готов к использованию
  },
  methods: {
    async initializeDoctor() {
      try {
        // Сначала проверяем тип пользователя
        const authResponse = await api.checkAuth()
        const authUser = authResponse.data.user || {}
        const userType = authUser.type
        this.currentStaffRole = authUser.staff_role || null

        if (this.isClinicManagerMode) {
          if (userType !== 'staff' || this.currentStaffRole !== 'clinic_manager') {
            return
          }

          const assignedClinic = authUser.assigned_clinic
          this.managerClinicId = typeof assignedClinic === 'object'
            ? (assignedClinic?.id || null)
            : assignedClinic

          await this.loadClinicDoctors()
          return
        }

        if (userType !== 'doctor') {
          return
        }
        
        // Получаем профиль врача
        const doctorProfile = await api.getDoctorProfile()
        this.doctorId = doctorProfile.data.id
        
        if (!this.doctorId) {
        }
      } catch (error) {
      // Error handled silently
      }
    },
    async loadClinicDoctors() {
      this.managerDoctors = []
      this.selectedDoctorId = ''

      if (!this.managerClinicId) return

      try {
        const response = await staffApi.getMedicalStaff({ type: 'doctor' })
        const medicalStaff = this.extractMedicalStaff(response)
        this.managerDoctors = medicalStaff
          .filter((staffMember) => !staffMember.type || staffMember.type === 'doctor')
          .map((staffMember) => ({
            id: staffMember.id,
            name: staffMember.name,
            specialization: staffMember.specialization,
            clinic_name: staffMember.clinic_name
          }))

        if (this.managerDoctors.length > 0) {
          this.selectedDoctorId = String(this.managerDoctors[0].id)
          this.doctorId = this.managerDoctors[0].id
        }
      } catch (error) {
        this.managerDoctors = []
      }
    },
    extractMedicalStaff(responsePayload) {
      if (!responsePayload) return []
      if (Array.isArray(responsePayload)) return responsePayload
      if (Array.isArray(responsePayload.medical_staff)) return responsePayload.medical_staff
      if (Array.isArray(responsePayload.results)) return responsePayload.results
      if (Array.isArray(responsePayload.data?.medical_staff)) return responsePayload.data.medical_staff
      if (Array.isArray(responsePayload.data?.results)) return responsePayload.data.results
      return []
    },
    getDoctorDisplayName(doctor) {
      if (!doctor) return 'Врач'

      if (doctor.name) return doctor.name
      if (doctor.user?.full_name) return doctor.user.full_name
      if (doctor.full_name) return doctor.full_name

      const parts = []
      if (doctor.user?.last_name) parts.push(doctor.user.last_name)
      if (doctor.user?.first_name) parts.push(doctor.user.first_name)
      if (doctor.user?.middle_name) parts.push(doctor.user.middle_name)
      return parts.join(' ') || `Врач #${doctor.id}`
    },
    
    initializeCalendar() {
      const today = new Date()
      this.selectedDate = new Date(today)
      this.currentWeekStart = this.getWeekStart(today)
      this.generateWeekDays()
      this.generateCalendarDays()
    },
    
    getWeekStart(date) {
      const d = new Date(date)
      const day = d.getDay()
      const diff = d.getDate() - day + (day === 0 ? -6 : 1) // adjust when day is sunday
      return new Date(d.setDate(diff))
    },
    
    generateWeekDays() {
      this.weekDays = []
      
      for (let i = 0; i < 7; i++) {
        const date = new Date(this.currentWeekStart)
        date.setDate(date.getDate() + i)
        
        this.weekDays.push({
          name: this.weekdayNames[i],
          day: date.getDate(),
          date: new Date(date),
          dateString: date.toISOString().split('T')[0]
        })
      }
    },
    
    generateCalendarDays() {
      // Используем GMT+5 (Казахстан) часовой пояс
      const now = new Date()
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000)
      const today = new Date(utc + (5 * 3600000)) // GMT+5
      
      const calendarDays = []
      
      // Показываем предыдущие 5 дней
      const startDate = new Date(today)
      startDate.setDate(today.getDate() - 5)
      
      // Показываем последующие 3 недели (21 день)
      const endDate = new Date(today)
      endDate.setDate(today.getDate() + 21)
      
      const currentDate = new Date(startDate)
      
      while (currentDate <= endDate) {
        const dayName = this.weekdayNamesWithSunday[currentDate.getDay()]
        
        calendarDays.push({
          name: dayName,
          day: currentDate.getDate(),
          date: new Date(currentDate),
          dateString: currentDate.toISOString().split('T')[0],
          month: this.monthNames[currentDate.getMonth()].substring(0, 3)
        })
        
        currentDate.setDate(currentDate.getDate() + 1)
      }
      
      this.calendarDays = calendarDays
      this.currentMonthYear = this.monthNames[today.getMonth()] + ' ' + today.getFullYear()
      
      // Центрируем на текущей дате
      this.$nextTick(() => {
        this.scrollToToday()
      })
    },
    
    scrollToToday() {
      // Используем GMT+5 (Казахстан) часовой пояс
      const now = new Date()
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000)
      const today = new Date(utc + (5 * 3600000)) // GMT+5
      const todayString = today.toISOString().split('T')[0]
      
      const todayIndex = this.calendarDays.findIndex(day => day.dateString === todayString)
      
      if (todayIndex !== -1 && this.$refs.calendarStrip) {
        const cardWidth = 88 // min-w-20 + space
        const stripWidth = this.$refs.calendarStrip.clientWidth
        const scrollPosition = (todayIndex * cardWidth) - (stripWidth / 2) + (cardWidth / 2)
        
        this.$refs.calendarStrip.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: 'smooth'
        })
      }
    },
    
    handleScroll() {
      // Метод может быть использован для других целей в будущем
    },
    
    formatWeekRange(weekStart) {
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekEnd.getDate() + 6)
      
      const startDay = weekStart.getDate()
      const startMonth = this.monthNames[weekStart.getMonth()].substring(0, 3)
      const endDay = weekEnd.getDate()
      const endMonth = this.monthNames[weekEnd.getMonth()].substring(0, 3)
      const endYear = weekEnd.getFullYear()
      
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${endYear}`
    },
    
    formatSelectedDate() {
      if (!this.selectedDate) return ''
      const weekdayLong = [
        this.$t('calendar.weekdays.sunday'),
        this.$t('calendar.weekdays.monday'),
        this.$t('calendar.weekdays.tuesday'),
        this.$t('calendar.weekdays.wednesday'),
        this.$t('calendar.weekdays.thursday'),
        this.$t('calendar.weekdays.friday'),
        this.$t('calendar.weekdays.saturday')
      ]
      const weekday = weekdayLong[this.selectedDate.getDay()]
      const day = this.selectedDate.getDate()
      const month = this.monthNames[this.selectedDate.getMonth()]
      const year = this.selectedDate.getFullYear()
      
      return `${weekday}, ${day} ${month} ${year}`
    },
    
    formatTime(timeString) {
      if (!timeString) return ''
      return timeString.substring(0, 5)
    },
    
    getDayAppointmentCount(day) {
      if (!this.doctorId) return 0
      return this.appointments.filter(appointment => {
        const slot = this.slotsData[appointment.slot]
        return slot && slot.date === day.dateString
      }).length
    },
    
    getDayAppointments(day) {
      if (!this.doctorId) return []
      return this.appointments.filter(appointment => {
        const slot = this.slotsData[appointment.slot]
        return slot && slot.date === day.dateString
      })
    },
    
    async fetchAppointments() {
      if (!this.doctorId) {
        this.appointments = []
        this.slotsData = {}
        this.patientsData = {}
        return
      }

      this.loadingAppointments = true
      
      try {
        const response = this.isClinicManagerMode
          ? await staffApi.getStaffAppointments({ doctor: this.doctorId })
          : await staffApi.getMyStaffAppointments()

        this.appointments = response.data.results || response.data || []
        
        await this.fetchAppointmentDetails()
        // Убираем автоматическую загрузку слотов - теперь загружаем только при клике на день
      } catch (error) {
      // Error handled silently
      } finally {
        this.loadingAppointments = false
      }
    },
    
    async fetchAppointmentDetails() {
      this.slotsData = {}
      this.patientsData = {}
      
      const slotIds = this.appointments.map(a => a.slot).filter(id => id)
      const patientIds = this.appointments.map(a => a.patient).filter(id => id)
      
      // Fetch slots
      for (const slotId of slotIds) {
        try {
          const slotResponse = await api.getSlotById(slotId)
          if (slotResponse.data) {
            this.slotsData[slotId] = slotResponse.data
          }
        } catch (error) {
      // Error handled silently
        }
      }
      
      // Fetch patients
      for (const patientId of patientIds) {
        try {
          const patientResponse = await api.getPatientById(patientId)
          if (patientResponse.data) {
            this.patientsData[patientId] = patientResponse.data
          }
      } catch (error) {
      // Error handled silently
        }
      }
    },
    
    async fetchDaySlots() {
      if (!this.selectedDate || !this.doctorId) return
      
      const selectedDateString = this.selectedDate.toISOString().split('T')[0]
      
      // Проверяем кеш
      if (this.cachedDaySlots[selectedDateString]) {
        const cachedData = this.cachedDaySlots[selectedDateString]
        this.dayTimeSlots = cachedData.dayTimeSlots
        this.selectedDayAppointments = cachedData.selectedDayAppointments
        return
      }
      
      this.loadingDaySlots = true
      
      try {
        // Получаем слоты врача на выбранную дату
        const slotsResponse = await api.getDoctorSlotsByDate(this.doctorId, selectedDateString)
        
        if (!slotsResponse.data.has_schedule) {
          // У врача нет расписания на этот день
          this.dayTimeSlots = []
          this.selectedDayAppointments = []
          
          // Кешируем пустой результат
          this.cachedDaySlots[selectedDateString] = {
            dayTimeSlots: [],
            selectedDayAppointments: []
          }
          return
        }
        
        const daySlots = slotsResponse.data.slots || []
        
        // Фильтруем appointments для выбранного дня
        this.selectedDayAppointments = this.appointments.filter(appointment => {
          const slot = this.slotsData[appointment.slot]
          return slot && slot.date === selectedDateString
        }).sort((a, b) => {
          const slotA = this.slotsData[a.slot]
          const slotB = this.slotsData[b.slot]
          if (!slotA || !slotB) return 0
          return slotA.start_time.localeCompare(slotB.start_time)
        })
        
        // Создаем слоты времени с информацией о записях
        this.dayTimeSlots = daySlots.map(slot => {
          // Находим запись для этого слота
          const appointment = this.selectedDayAppointments.find(appt => 
            this.slotsData[appt.slot] && this.slotsData[appt.slot].id === slot.id
          )
          
          return {
            time: slot.start_time.substring(0, 5), // Формат HH:MM
            slotId: slot.id,
            appointment: appointment || null,
            slot: slot
          }
        })
        
        // Кешируем результат
        this.cachedDaySlots[selectedDateString] = {
          dayTimeSlots: this.dayTimeSlots,
          selectedDayAppointments: this.selectedDayAppointments
        }
        
      } catch (error) {
        this.dayTimeSlots = []
        this.selectedDayAppointments = []
      } finally {
        this.loadingDaySlots = false
      }
    },
    

    
    getSlotStartTime(appointment) {
      const slot = this.slotsData[appointment.slot]
      return slot ? slot.start_time : ''
    },
    
    getSlotEndTime(appointment) {
      const slot = this.slotsData[appointment.slot]
      return slot ? slot.end_time : ''
    },
    
    getPatientName(appointment) {
      const patient = this.patientsData[appointment.patient]
      if (patient && patient.user) {
        const parts = []
        if (patient.user.last_name) parts.push(patient.user.last_name)
        if (patient.user.first_name) parts.push(patient.user.first_name)
        if (patient.user.middle_name) parts.push(patient.user.middle_name)
        return parts.join(' ') || 'Пациент'
      }
      return 'Пациент'
    },
    
    getStatusText(status) {
      const statusMap = {
        scheduled: this.$t('appointment.scheduled'),
        in_progress: this.$t('appointment.inProgress'), 
        completed: this.$t('appointment.completed'),
        cancelled: this.$t('appointment.cancelled'),
        no_show: this.$t('appointment.no_show'),
        patient_no_show: this.$t('appointment.patient_no_show')
      }
      return statusMap[status] || status
    },
    
    async startConsultation(appointment) {
      try {
        await api.updateAppointmentStatus(appointment.id, 'in_progress')
        appointment.status = 'in_progress'
      } catch (error) {
      // Error handled silently
      }
    },
    
    async cancelAppointment(appointment) {
      if (confirm('Вы уверены, что хотите отменить этот прием?')) {
        try {
          await api.cancelAppointment(appointment.id)
          appointment.status = 'cancelled'
        } catch (error) {
      // Error handled silently
        }
      }
    },
    
    async selectDay(day) {
      this.selectedDate = new Date(day.date)
      await this.fetchDaySlots()
    },
    
    isSelectedDay(day) {
      if (!this.selectedDate) return false
      return day.dateString === this.selectedDate.toISOString().split('T')[0]
    },
    
    isToday(day) {
      // Используем GMT+5 (Казахстан) часовой пояс
      const now = new Date()
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000)
      const today = new Date(utc + (5 * 3600000)) // GMT+5
      const todayString = today.toISOString().split('T')[0]
      return day.dateString === todayString
    },
    
    async goToToday() {
      // Используем GMT+5 (Казахстан) часовой пояс
      const now = new Date()
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000)
      const today = new Date(utc + (5 * 3600000)) // GMT+5
      this.selectedDate = new Date(today)
      this.scrollToToday()
      await this.fetchDaySlots()
    },
    
    startDrag(event) {
      this.isDragging = true
      this.dragStartX = event.clientX
      this.dragStartScrollLeft = this.$refs.calendarStrip.scrollLeft
      event.preventDefault()
    },
    
    handleDrag(event) {
      if (!this.isDragging) return
      
      event.preventDefault()
      const deltaX = this.dragStartX - event.clientX
      const newScrollLeft = this.dragStartScrollLeft + deltaX
      
      this.$refs.calendarStrip.scrollLeft = newScrollLeft
    },
    
    endDrag() {
      this.isDragging = false
    },

    initializeCalendarPicker() {
      const today = new Date()
      this.pickerMonth = today.getMonth()
      this.pickerYear = today.getFullYear()
      
      // Generate available years (current year ± 5 years)
      this.availableYears = []
      for (let year = today.getFullYear() - 5; year <= today.getFullYear() + 5; year++) {
        this.availableYears.push(year)
      }
      
      this.generatePickerCalendar()
    },

    navigateYear(delta) {
      this.pickerYear = this.pickerYear + delta
      this.updatePickerCalendar()
    },

    navigateMonth(delta) {
      let newMonth = this.pickerMonth + delta
      let newYear = this.pickerYear
      
      if (newMonth < 0) {
        newMonth = 11
        newYear -= 1
      } else if (newMonth > 11) {
        newMonth = 0
        newYear += 1
      }
      
      this.pickerMonth = newMonth
      this.pickerYear = newYear
      this.updatePickerCalendar()
    },

    updatePickerCalendar() {
      this.generatePickerCalendar()
    },

    generatePickerCalendar() {
      const year = this.pickerYear
      const month = this.pickerMonth
      const firstDay = new Date(year, month, 1)
      const lastDay = new Date(year, month + 1, 0)
      const daysInMonth = lastDay.getDate()
      
      // Get first day of week (Monday = 1, Sunday = 0, adjust for Monday start)
      let startDay = firstDay.getDay()
      startDay = startDay === 0 ? 6 : startDay - 1 // Convert Sunday=0 to 6, others -1
      
      const days = []
      
      // Add empty cells for days before month starts
      for (let i = 0; i < startDay; i++) {
        const prevMonth = month === 0 ? 11 : month - 1
        const prevYear = month === 0 ? year - 1 : year
        const prevMonthDays = new Date(prevYear, prevMonth + 1, 0).getDate()
        const day = prevMonthDays - startDay + i + 1
        
        days.push({
          key: `prev-${i}`,
          day: day,
          date: new Date(prevYear, prevMonth, day),
          isCurrentMonth: false,
          isSelected: false,
          isToday: false,
          isDisabled: false,
          hasAppointments: false
        })
      }
      
      // Add days of current month
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day)
        const dateString = date.toISOString().split('T')[0]
        const isSelected = this.selectedDate && dateString === this.selectedDate.toISOString().split('T')[0]
        // Используем GMT+5 (Казахстан) часовой пояс для определения сегодняшнего дня
        const now = new Date()
        const utc = now.getTime() + (now.getTimezoneOffset() * 60000)
        const today = new Date(utc + (5 * 3600000)) // GMT+5
        const isToday = dateString === today.toISOString().split('T')[0]
        const hasAppointments = this.getDayAppointmentCount({ dateString }) > 0
        
        days.push({
          key: `current-${day}`,
          day: day,
          date: new Date(date),
          isCurrentMonth: true,
          isSelected: isSelected,
          isToday: isToday,
          isDisabled: false,
          hasAppointments: hasAppointments
        })
      }
      
      // Fill remaining cells to complete the grid (6 weeks = 42 days)
      const remainingDays = 42 - days.length
      for (let i = 1; i <= remainingDays; i++) {
        const nextMonth = month === 11 ? 0 : month + 1
        const nextYear = month === 11 ? year + 1 : year
        const date = new Date(nextYear, nextMonth, i)
        
        days.push({
          key: `next-${i}`,
          day: i,
          date: new Date(date),
          isCurrentMonth: false,
          isSelected: false,
          isToday: false,
          isDisabled: false,
          hasAppointments: false
        })
      }
      
      this.pickerCalendarDays = days
    },

    selectDateFromPicker(day) {
      if (!day.isDisabled) {
        this.selectedDate = new Date(day.date)
        
        // If selecting a date from prev/next month, update picker month
        if (!day.isCurrentMonth) {
          this.pickerMonth = day.date.getMonth()
          this.pickerYear = day.date.getFullYear()
          this.updatePickerCalendar()
        } else {
          // Just update selection for current month
          this.generatePickerCalendar()
        }
      }
    },

    async goToTodayFromPicker() {
      // Используем GMT+5 (Казахстан) часовой пояс
      const now = new Date()
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000)
      const today = new Date(utc + (5 * 3600000)) // GMT+5
      this.selectedDate = new Date(today)
      this.pickerMonth = today.getMonth()
      this.pickerYear = today.getFullYear()
      this.generatePickerCalendar()
      this.scrollToToday()
      await this.fetchDaySlots()
      this.showCalendarPicker = false
    },

    async confirmDateSelection() {
      this.scrollToSelectedDate()
      await this.fetchDaySlots()
      this.showCalendarPicker = false
    },
    
    scrollToSelectedDate() {
      if (!this.selectedDate) return
      
      const selectedDateString = this.selectedDate.toISOString().split('T')[0]
      const selectedIndex = this.calendarDays.findIndex(day => day.dateString === selectedDateString)
      
      if (selectedIndex !== -1 && this.$refs.calendarStrip) {
        const cardWidth = 88 // min-w-20 + space
        const stripWidth = this.$refs.calendarStrip.clientWidth
        const scrollPosition = (selectedIndex * cardWidth) - (stripWidth / 2) + (cardWidth / 2)
        
        this.$refs.calendarStrip.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: 'smooth'
        })
      }
    }
  }
}
</script>

<style scoped>
.bg-blue-25 {
  background-color: rgb(219 234 254 / 0.3);
}

.ml-13 {
  margin-left: 3.25rem;
}

/* Hide scrollbar for calendar strip */
.overflow-x-auto::-webkit-scrollbar {
  display: none;
}

.overflow-x-auto {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Drag and drop cursors */
.cursor-grab {
  cursor: grab;
}

.cursor-grab:active,
.active\:cursor-grabbing:active {
  cursor: grabbing;
}

/* Prevent text selection during drag */
.select-none {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* Calendar day cards */
.min-w-20 {
  min-width: 5rem;
}

/* Hover effect animations */
.group:hover .group-hover\:scale-105 {
  transform: scale(1.05);
}

/* Tooltip styles */
.group:hover .invisible {
  visibility: visible;
  opacity: 1;
}

.invisible {
  opacity: 0;
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
}

/* Custom scrollbar for time slots list */
.space-y-2 {
  max-height: 70vh;
  overflow-y: auto;
}

.space-y-2::-webkit-scrollbar {
  width: 6px;
}

.space-y-2::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.space-y-2::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.space-y-2::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Smooth transitions */
.transition-all {
  transition: all 0.2s ease-in-out;
}

/* Loading spinner enhancement */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Pulse animation for in-progress indicator */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Hover effects for slots */
.group:hover {
  transform: translateY(-1px);
}

/* Z-index management for tooltips */
.group {
  position: relative;
}

.group .absolute.z-10 {
  z-index: 50;
}

/* Smooth scrolling */
.scroll-smooth {
  scroll-behavior: smooth;
}

/* Calendar strip enhancements */
.calendar-day-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.calendar-day-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Today indicator pulse */
.today-indicator {
  animation: pulse-subtle 2s infinite;
}

@keyframes pulse-subtle {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
}

/* Calendar Picker Modal Styles */
.fixed.inset-0 {
  backdrop-filter: blur(4px);
}

/* Calendar picker animations */
.calendar-picker-modal {
  animation: fadeInScale 0.2s ease-out;
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Calendar grid hover effects */
.calendar-day-button {
  position: relative;
  transition: all 0.15s ease-in-out;
}

.calendar-day-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.calendar-day-button:active {
  transform: translateY(0);
}

/* Focus styles for accessibility */
.calendar-day-button:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
}

/* Dropdown styles */
select:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

/* Modal backdrop animation */
.modal-backdrop {
  animation: fadeIn 0.15s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Calendar picker button hover effect */
.calendar-picker-button {
  transition: all 0.2s ease-in-out;
}

.calendar-picker-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Appointment indicator dot */
.appointment-dot {
  transition: all 0.2s ease-in-out;
}

/* Selected date ring effect */
.selected-day {
  position: relative;
}

.selected-day::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 12px;
  background: linear-gradient(45deg, #3b82f6, #1d4ed8);
  z-index: -1;
}

/* Month navigation smooth transitions */
.month-nav-button {
  transition: all 0.2s ease-in-out;
}

.month-nav-button:hover {
  transform: scale(1.1);
  background-color: rgba(59, 130, 246, 0.1);
}
</style>
